import { useState, useEffect, useCallback } from 'react'
import { Alert } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { RouteComponentProps } from 'react-router-dom'
import { Loader } from '@components/loader'
import { useTemplate } from '@global/hooks/useTemplate'
import { useAppSelector } from '@redux/hooks'
import { api } from 'api'
import { SubmissionSelect, OverallStatus, OutboundDestinationsTable } from './components'
import { ErrorAlert } from './components/error'
import * as css from './styled'
import { DatasetSubmissionOutboundIntegration, SubmissionWithModDatasetPosts } from './types'
import { useQueryPendingRollupDataset } from '../../../templates/hooks/useQueryPending'
import { t } from '@lingui/core/macro'

function submissionHasErrors(submission) {
  const hasInternalErrors = Boolean(
    submission?.status !== 'Complete' &&
      submission?.errors &&
      submission?.errors?.length > 0 &&
      submission?.errors?.some(err => err.type_code >= 5000 && err.type_code < 6000),
  )
  const hasDeletedFieldsError = Boolean(submission?.errors?.some(err => err.type_code === 4500))
  return hasInternalErrors || hasDeletedFieldsError
}

async function getDatasetHistorySubmissions(datasetId) {
  const withParams = [
    'submitted_by_user:id,avatar_slug,name',
    'dataset_posts',
    'dataset_posts.target',
    'dataset_posts.target.connector.connector_type',
    'dataset_posts.multi_target',
    'errors',
    'number_of_rows',
    'dataset',
  ]
    .map(param => `with[]=${param}`)
    .join('&')

  const url = `/api/v1/account/dataset/${datasetId}/submissions?${withParams}`

  const { data } = await api.get(url)
  return data
}

interface DatasetHistoryV2Params {
  id: string
}
export const DatasetHistoryV2 = ({ match }: RouteComponentProps<DatasetHistoryV2Params>) => {
  const dsId = match.params.id
  const [outboundIntegrations, setOutboundIntegrations] = useState<
    DatasetSubmissionOutboundIntegration[]
  >([])
  const [selectedSubmission, setSelectedSubmission] = useState<number | null>(null)
  const [apiCatchErrors, setApiCatchErrors] = useState<string[]>([])

  const meta = useAppSelector(state => state.Datasets.datasetMetadataById[dsId])
  const { template } = useTemplate({ templateId: meta?.template_id })

  const { pendingRollupDataset, isLoading: isLoadingPending } = useQueryPendingRollupDataset({
    datasetId: String(dsId),
  })

  const { data: datasetSubmissions, isInitialLoading: isLoadingSubmissions } = useQuery<
    SubmissionWithModDatasetPosts[]
  >({
    queryKey: ['datasetHistorySubmissions', +dsId],
    queryFn: () => getDatasetHistorySubmissions(dsId),
  })

  const submissions = pendingRollupDataset?.submissions || datasetSubmissions

  const selectedSubmissionData = submissions?.find(({ id }) => id === selectedSubmission) as
    | SubmissionWithModDatasetPosts
    | undefined

  async function requestOutboundIntegrations(datasetSubmissionId) {
    try {
      const { data } = await api.get(`/api/core/v1/outbound-orchestration/${datasetSubmissionId}`)
      if (Object.values(data).length) {
        setOutboundIntegrations(data)
      }
    } catch (error) {
      const err = error as any
      const errorMessage = t`An error occured while requesting outbound integrations.`
      if (err?.status === 500) {
        setApiCatchErrors(prev => [...prev, errorMessage])
      } else {
        setApiCatchErrors(prev => [...prev, `${errorMessage} ${t`Status`} ${err?.status}`])
      }
    }
  }

  useEffect(() => {
    const id = submissions?.[0]?.id
    setSelectedSubmission(id ? +id : null)
  }, [submissions])

  useEffect(() => {
    if (selectedSubmission) {
      void requestOutboundIntegrations(selectedSubmission)
    }
  }, [selectedSubmission, selectedSubmissionData])

  const getSelectedSubmissionData = useCallback(
    () => selectedSubmissionData,
    [selectedSubmissionData],
  )

  if (isLoadingPending || isLoadingSubmissions) return <Loader />

  return (
    <css.DatasetHistoryWrapper>
      {submissions && selectedSubmission && selectedSubmissionData && (
        <>
          <div style={{ padding: '0 60px' }}>
            <div>
              <SubmissionSelect
                submissions={submissions}
                value={selectedSubmission}
                onChange={event => {
                  setSelectedSubmission(parseInt(event.target.value, 10))
                }}
              />
            </div>
            <OverallStatus
              submission={selectedSubmissionData}
              creativeClassificationEnabled={
                Boolean(template?.creativeConfiguration?.length) ||
                template?.template_fields?.some(
                  templateField => templateField?.field?.governance_type === 'creative',
                ) ||
                false
              }
            />
          </div>
          {apiCatchErrors.length > 0 ? (
            <Alert
              severity="error"
              sx={{
                backgroundColor: '#FEECEB',
                color: '#5f2120',
                maxWidth: 400,
                marginLeft: 'auto',
                marginRight: 'auto',
              }}
            >
              {apiCatchErrors.join('\n')}
            </Alert>
          ) : null}
          {submissionHasErrors(selectedSubmissionData)
            ? selectedSubmissionData?.errors?.map(error => (
                <ErrorAlert key={error.id} error={error} />
              ))
            : null}
          <OutboundDestinationsTable
            outboundIntegrations={outboundIntegrations}
            datasetSubmissionId={selectedSubmission}
            getSelectedSubmissionData={getSelectedSubmissionData}
          />
        </>
      )}
    </css.DatasetHistoryWrapper>
  )
}
